import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api, API_BASE } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { IoChevronDown } from 'react-icons/io5';

const SYNC_ETA_TEXT = 'Estimated time: 30 seconds to 2 minutes (depends on product count and image lookup).';

// Embedded defaults (same as backend .env); pre-fill so you rarely need to type. Override in inputs when using a different sheet/folder.
const EMBEDDED_SPREADSHEET_ID = '1AAJzj6qkxZIgnxf3knTstM74fC_X_-6rKbEMHSCmCe4';
const EMBEDDED_DRIVE_FOLDER_ID = '1wS32zv_C2Uys5imEmGEUXLWqFlkDjMVY';

// Default column letters (must match backend DEFAULT_COLUMN_MAP). Used for placeholders and building column_map overrides.
const DEFAULT_COLUMNS = {
  code: 'A', name: 'S', category: 'I', size: 'J', color: 'K', material: 'L', gender: 'M',
  rent_1_day: 'O', security_deposit: 'P', image_1: 'D', description: 'Y', short_description: 'AA',
  seo_title: 'U', meta_title: 'V', meta_keyphrase: 'W', product_size: 'AG', weight_grams: 'AF'
};

function SummaryAlert({ result }) {
  if (!result) return null;
  if (result._stopped) {
    return <Alert severity="info" sx={{ mt: 2 }}>Sync stopped by user. Partial data may have been synced.</Alert>;
  }
  return (
    <>
      <Alert severity="success" sx={{ mb: 1 }}>
        Categories created: {result.categoriesCreated ?? 0}, matched: {result.categoriesMatched ?? 0}.
        Products created: {result.productsCreated ?? 0}, updated: {result.productsUpdated ?? 0}.
        {result.imagesResolved != null && result.imagesResolved > 0 && (
          <> Image URLs resolved: {result.imagesResolved}.</>
        )}
        {result.imagesDownloaded != null && result.imagesDownloaded > 0 && (
          <> Images downloaded to server: {result.imagesDownloaded}.</>
        )}
        {(result.productsCreated > 0 || result.productsUpdated > 0) && (
          <> <Link to="/admin/products">View products</Link> to see synced items and images.</>
        )}
        {result.imagesResolved > 0 && result.imagesDownloaded === 0 && (
          <> Images resolved but none downloaded. Check: Drive folder is shared with your Google account (or service account), and sheet columns D–G contain filenames that exist in that folder or full Drive links.</>
        )}
      </Alert>
      {result.errors && result.errors.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2" component="span">Errors:</Typography>
          <Box component="ul" sx={{ margin: 0, pl: 2 }}>
            {result.errors.map((e, i) => <li key={i}>{e}</li>)}
          </Box>
        </Alert>
      )}
    </>
  );
}

function SectionTitle({ children, first }) {
  return (
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5, mt: first ? 0 : 2 }}>
      {children}
    </Typography>
  );
}

export default function AdminSync() {
  const [loading, setLoading] = useState(false);

  const [spreadsheetId, setSpreadsheetId] = useState(EMBEDDED_SPREADSHEET_ID);
  const [sheetListLoading, setSheetListLoading] = useState(false);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [imageBaseUrl, setImageBaseUrl] = useState('');
  const [imageDriveFolderId, setImageDriveFolderId] = useState(EMBEDDED_DRIVE_FOLDER_ID);
  const [sheetsResult, setSheetsResult] = useState(null);
  const [sheetsError, setSheetsError] = useState('');
  const [googleStatus, setGoogleStatus] = useState({ connected: false, email: null, oauthConfigured: false });
  const [googleStatusLoading, setGoogleStatusLoading] = useState(false);
  const [googleMessage, setGoogleMessage] = useState(null);
  const sheetsDefaultsLoaded = useRef(false);
  const [columnOverrides, setColumnOverrides] = useState({});
  const [mappingExpanded, setMappingExpanded] = useState(true);
  const syncAbortRef = useRef(null);
  const [testImageFilename, setTestImageFilename] = useState('');
  const [testImageLoading, setTestImageLoading] = useState(false);
  const [testImageResult, setTestImageResult] = useState(null);

  const loadSheetsDefaults = async () => {
    if (sheetsDefaultsLoaded.current) return;
    try {
      const d = await api('/api/admin/sync/sheets/defaults');
      sheetsDefaultsLoaded.current = true;
      if (d.spreadsheet_id) setSpreadsheetId(d.spreadsheet_id);
      if (d.image_drive_folder_id) setImageDriveFolderId(d.image_drive_folder_id);
    } catch (_) { /* ignore */ }
  };

  const loadGoogleStatus = async () => {
    setGoogleStatusLoading(true);
    try {
      const data = await api('/api/admin/google/status');
      setGoogleStatus({
        connected: !!data.connected,
        email: data.email || null,
        oauthConfigured: !!data.oauthConfigured,
      });
    } catch (_) {
      setGoogleStatus({ connected: false, email: null, oauthConfigured: false });
    } finally {
      setGoogleStatusLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get('google');
    const message = params.get('message');
    if (g === 'connected') {
      setGoogleMessage({ type: 'success', text: 'Google account connected. You can now load sheets.' });
      window.history.replaceState({}, '', window.location.pathname);
      loadGoogleStatus();
    } else if (g === 'error' && message) {
      setGoogleMessage({ type: 'error', text: decodeURIComponent(message) });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    loadGoogleStatus();
    loadSheetsDefaults();
  }, []);

  const handleConnectGoogle = () => {
    const redirectUri = window.location.origin + window.location.pathname;
    window.location.href = `${API_BASE}/api/admin/google/auth?redirect_uri=${encodeURIComponent(redirectUri)}`;
  };

  const handleDisconnectGoogle = async () => {
    try {
      await api('/api/admin/google/disconnect', { method: 'POST' });
      setGoogleStatus((prev) => ({ ...prev, connected: false, email: null }));
      setGoogleMessage({ type: 'info', text: 'Google account disconnected.' });
    } catch (err) {
      setGoogleMessage({ type: 'error', text: err.message || 'Disconnect failed' });
    }
  };

  const handleLoadSheets = async () => {
    if (!spreadsheetId.trim()) {
      setSheetsError('Enter spreadsheet ID');
      return;
    }
    setSheetsError('');
    setSheetListLoading(true);
    try {
      const data = await api(`/api/admin/sync/sheets/list?spreadsheet_id=${encodeURIComponent(spreadsheetId.trim())}`);
      setSheetNames(data.sheet_names || []);
      setSelectedSheets(data.sheet_names || []);
    } catch (err) {
      setSheetsError(err.message || 'Failed to list sheets');
      setSheetNames([]);
    } finally {
      setSheetListLoading(false);
    }
  };

  const toggleSheet = (name) => {
    setSelectedSheets((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );
  };

  const selectAllSheets = () => setSelectedSheets([...sheetNames]);
  const selectNoneSheets = () => setSelectedSheets([]);

  const handleSheetsSync = async () => {
    if (!spreadsheetId.trim()) {
      setSheetsError('Enter spreadsheet ID');
      return;
    }
    setSheetsError('');
    setSheetsResult(null);
    const controller = new AbortController();
    syncAbortRef.current = controller;
    setLoading(true);
    try {
      const column_map = {};
      Object.keys(DEFAULT_COLUMNS).forEach((key) => {
        const v = columnOverrides[key];
        if (v && String(v).trim()) {
          const letter = String(v).trim().toUpperCase().replace(/[^A-Z]/g, '');
          if (letter) column_map[key] = letter;
        }
      });
      const body = {
        spreadsheet_id: spreadsheetId.trim(),
        sheet_names: selectedSheets,
        image_base_url: imageBaseUrl.trim() || undefined,
        image_drive_folder_id: imageDriveFolderId.trim() || undefined,
        download_images_locally: true,
        ...(Object.keys(column_map).length > 0 && { column_map }),
      };
      const data = await api('/api/admin/sync/sheets', {
        method: 'POST',
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      setSheetsResult(data.summary || data);
    } catch (err) {
      if (err.name === 'AbortError') {
        setSheetsError('');
        setSheetsResult({ productsCreated: 0, productsUpdated: 0, categoriesCreated: 0, categoriesMatched: 0, errors: [], imagesResolved: 0, imagesDownloaded: 0, _stopped: true });
      } else {
        setSheetsError(err.message || 'Sheets sync failed');
      }
    } finally {
      syncAbortRef.current = null;
      setLoading(false);
    }
  };

  const handleStopSync = () => {
    if (syncAbortRef.current) {
      syncAbortRef.current.abort();
    }
  };

  const handleTestImage = async () => {
    const fn = testImageFilename.trim();
    if (!fn) return;
    setTestImageResult(null);
    setTestImageLoading(true);
    try {
      const data = await api('/api/admin/sync/test-image', {
        method: 'POST',
        body: JSON.stringify({
          filename: fn,
          image_drive_folder_id: imageDriveFolderId.trim() || undefined,
          image_base_url: imageBaseUrl.trim() || undefined,
        }),
      });
      setTestImageResult(data);
    } catch (err) {
      setTestImageResult({ resolved: false, error: err.message || 'Test failed' });
    } finally {
      setTestImageLoading(false);
    }
  };

  return (
    <Card>
      {loading && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'error.light',
            color: 'error.contrastText',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            boxShadow: 2,
          }}
        >
          <LinearProgress sx={{ flex: 1, minWidth: 120, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} />
          <Typography variant="body2" fontWeight={600}>Syncing…</Typography>
          <Button variant="contained" onClick={handleStopSync} size="small" sx={{ bgcolor: 'white', color: 'error.main', '&:hover': { bgcolor: 'grey.100' } }}>
            Stop sync
          </Button>
        </Box>
      )}
      <CardHeader
        title="Sync"
        subheader="Sheets sync — product data and images from your spreadsheet and Drive folder."
      />
      <CardContent>
        <Box>
            {/* Column mapping reference — visible at top so sheet layout can be matched */}
            <Accordion expanded={mappingExpanded} onChange={() => setMappingExpanded((e) => !e)} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<IoChevronDown />}>
                <Typography fontWeight={600}>Column mapping reference (sheet columns → system fields)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Which spreadsheet column maps to which system field. Default letters match backend. Use &quot;Custom column overrides&quot; below if your sheet uses different columns.
                </Typography>
                <TableContainer sx={{ maxWidth: 720, border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                  <Table size="small" aria-label="Column mapping">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Spreadsheet column</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>System item</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow><TableCell>A</TableCell><TableCell>SKU (GemCode)</TableCell><TableCell>Product code; required</TableCell></TableRow>
                      <TableRow><TableCell>B</TableCell><TableCell>MidcCode</TableCell><TableCell>Not synced</TableCell></TableRow>
                      <TableRow><TableCell>C</TableCell><TableCell>Mannequin Photos</TableCell><TableCell>Not synced (we use D–G for product images)</TableCell></TableRow>
                      <TableRow><TableCell>D</TableCell><TableCell>Main image</TableCell><TableCell>Primary product image. Use filename that matches Drive (e.g. GEM-AKS-001-2.png).</TableCell></TableRow>
                      <TableRow><TableCell>E, F, G</TableCell><TableCell>Secondary images</TableCell><TableCell>Up to 3 additional images. Same naming: GEM-AKS-XXX-N.png.</TableCell></TableRow>
                      <TableRow><TableCell>I</TableCell><TableCell>Category</TableCell><TableCell>Synced with system categories; or sheet name if empty</TableCell></TableRow>
                      <TableRow><TableCell>J</TableCell><TableCell>Size</TableCell><TableCell>Synced with <a href="/admin/variations">Variations</a> (Size)</TableCell></TableRow>
                      <TableRow><TableCell>K</TableCell><TableCell>Color</TableCell><TableCell>Synced with <a href="/admin/variations">Variations</a> (Color)</TableCell></TableRow>
                      <TableRow><TableCell>L</TableCell><TableCell>Material</TableCell><TableCell>Synced with <a href="/admin/variations">Variations</a> (Material)</TableCell></TableRow>
                      <TableRow><TableCell>M</TableCell><TableCell>Gender</TableCell><TableCell /></TableRow>
                      <TableRow><TableCell>P</TableCell><TableCell>Security deposit</TableCell><TableCell>INR (₹)</TableCell></TableRow>
                      <TableRow><TableCell>O</TableCell><TableCell>Rent (1 day)</TableCell><TableCell>INR (₹)</TableCell></TableRow>
                      <TableRow><TableCell>S</TableCell><TableCell>Product name</TableCell><TableCell>Name from sheet</TableCell></TableRow>
                      <TableRow><TableCell>U–W</TableCell><TableCell>SEO / Meta</TableCell><TableCell>seo_title, meta_title, meta_keyphrase</TableCell></TableRow>
                      <TableRow><TableCell>X</TableCell><TableCell>Slug</TableCell><TableCell>Auto-generated from code + name (not from sheet)</TableCell></TableRow>
                      <TableRow><TableCell>Y</TableCell><TableCell>Description</TableCell><TableCell>Product description</TableCell></TableRow>
                      <TableRow><TableCell>AA</TableCell><TableCell>Short description</TableCell><TableCell>Skipped (not synced)</TableCell></TableRow>
                      <TableRow><TableCell>AF</TableCell><TableCell>Weight (grams)</TableCell><TableCell>Product weight</TableCell></TableRow>
                      <TableRow><TableCell>AG</TableCell><TableCell>Product size</TableCell><TableCell>Physical size (e.g. length)</TableCell></TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Add Product form fields map to the same columns: Name→S, SKU→A, Description→Y, Category→I (or sheet name), Size→J, Color→K, Material→L (synced with Variations), Images→D (main), E, F, G (secondary), Security deposit→P, Rent→O, etc.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {googleMessage && (
              <Alert severity={googleMessage.type} sx={{ mb: 2 }} onClose={() => setGoogleMessage(null)}>
                {googleMessage.text}
              </Alert>
            )}

            {/* 1. Google account */}
            <SectionTitle first>1. Google account</SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Sign in so the app can read your spreadsheet. Your sheet must be shared with this account (or use a service account).
            </Typography>
            {googleStatus.oauthConfigured ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {googleStatus.connected ? (
                  <>
                    <Typography variant="body2">
                      Connected as <strong>{googleStatus.email || 'Google account'}</strong>
                    </Typography>
                    <Button size="small" variant="outlined" color="secondary" onClick={handleDisconnectGoogle}>
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<span style={{ fontWeight: 'bold' }}>G</span>}
                    onClick={handleConnectGoogle}
                    disabled={googleStatusLoading}
                  >
                    Sign in with Google
                  </Button>
                )}
              </Box>
            ) : (
              <Alert severity="info" sx={{ py: 1 }}>
                Add OAuth credentials in backend .env, then&nbsp;
                <Button size="small" variant="outlined" onClick={handleConnectGoogle}>Sign in with Google</Button>
                &nbsp;— or share the sheet with your service account email as Viewer.
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            {/* 2. Spreadsheet */}
            <SectionTitle>2. Spreadsheet</SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Pre-filled from embedded settings. Change only if using a different spreadsheet.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Spreadsheet ID"
                placeholder="From URL: docs.google.com/spreadsheets/d/SPREADSHEET_ID/edit"
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                sx={{ minWidth: 360 }}
                fullWidth
              />
              <Button variant="outlined" onClick={handleLoadSheets} disabled={sheetListLoading}>
                {sheetListLoading ? 'Loading…' : 'Load sheets'}
              </Button>
            </Box>

            {/* Custom column overrides — if your sheet has different column letters, set them here */}
            <SectionTitle>Custom column overrides (optional)</SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              If your spreadsheet uses different column letters, enter the letter for each field. Leave blank to use defaults (A=SKU, S=Name, I=Category, P=Security deposit, O=Rent (1 day), D=Image 1, Y=Description, etc.).
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5, maxWidth: 560, mb: 2 }}>
              {['code', 'name', 'category', 'size', 'color', 'rent_1_day', 'security_deposit', 'image_1', 'description'].map((key) => (
                <TextField
                  key={key}
                  size="small"
                  label={key.replace(/_/g, ' ')}
                  placeholder={DEFAULT_COLUMNS[key] || ''}
                  value={columnOverrides[key] ?? ''}
                  onChange={(e) => setColumnOverrides((prev) => ({ ...prev, [key]: e.target.value }))}
                  inputProps={{ style: { textTransform: 'uppercase' }, maxLength: 3 }}
                  sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
                />
              ))}
            </Box>
            {/* 3. Sheet selection — always visible so you can choose which tabs to sync */}
            <SectionTitle>3. Sheet selection</SectionTitle>
            {sheetNames.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Choose which tabs to sync. Row 1 = headers; data starts from row 2.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Button size="small" variant="outlined" onClick={selectAllSheets}>Select all</Button>
                  <Button size="small" variant="outlined" onClick={selectNoneSheets}>Select none</Button>
                </Box>
                <FormGroup row sx={{ maxHeight: 200, overflow: 'auto', flexWrap: 'wrap', gap: 0, pl: 0.5, border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                  {sheetNames.map((name) => (
                    <FormControlLabel
                      key={name}
                      control={<Checkbox checked={selectedSheets.includes(name)} onChange={() => toggleSheet(name)} />}
                      label={name}
                      sx={{ mr: 2, mb: 0.5 }}
                    />
                  ))}
                </FormGroup>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {selectedSheets.length} sheet(s) selected for sync.
                </Typography>
              </>
            ) : (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Load your spreadsheet tabs to choose which sheets to sync. Enter the Spreadsheet ID above, then click &quot;Load sheets&quot;.
                </Typography>
                <Button variant="outlined" size="small" onClick={handleLoadSheets} disabled={sheetListLoading || !spreadsheetId.trim()}>
                  {sheetListLoading ? 'Loading…' : 'Load sheets'}
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* 4. Sync settings */}
            <SectionTitle>4. Sync settings</SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Images: use a base URL if filenames are on a CDN, or a Drive folder ID to resolve filenames from Google Drive (e.g. root folder with subfolders like GEM-AKS).
            </Typography>
            <Box sx={{ display: 'grid', gap: 2, maxWidth: 560 }}>
              <TextField
                size="small"
                label="Image base URL"
                placeholder="https://yoursite.com/images/"
                value={imageBaseUrl}
                onChange={(e) => setImageBaseUrl(e.target.value)}
                helperText="Optional. Leave empty if using Drive folder below."
                fullWidth
              />
              <TextField
                size="small"
                label="Drive folder ID (for images)"
                placeholder="From .../folders/FOLDER_ID"
                value={imageDriveFolderId}
                onChange={(e) => setImageDriveFolderId(e.target.value)}
                helperText="Pre-filled from embedded settings. Change only if using a different folder."
                fullWidth
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                All amounts are in <strong>INR (₹)</strong>. Sheet values for Rent (O) and Security deposit (P) are in rupees (e.g. 500 → ₹500).
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, fontWeight: 600 }}>
                Expected sheet layout
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Row 1 = header, data from row 2. Columns: A=Code, S=Name, I=Category, J=Size, K=Color, L=Material, M=Gender, O=Rent (1 day), P=Security deposit, Y=Description, AF=Weight, AG=Product size. D/E/F/G = image filenames.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Images are always downloaded to the server when you sync (saved under <code>uploads/product-images/</code>).
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                When enabled, images from Drive or base URL are saved to <code>backend/uploads/product-images/</code> and the site serves them from your server.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 600 }}>For all images to download from Drive:</Typography>
              <Box component="ul" sx={{ margin: 0, pl: 2, color: 'text.secondary', typography: 'body2' }}>
                <li>Sign in with Google on this page, or use a service account and share the Drive folder with its email (Viewer).</li>
                <li>Set the Drive folder ID above (root folder that contains your image files or subfolders like GEM-AKS).</li>
                <li>Drive folder pattern must match image names: e.g. GEM → GEM-AKS → GEM-AKS-001 with files GEM-AKS-001-1.png, GEM-AKS-001-2.png, …</li>
                <li>In the sheet, columns D (main), E, F, G must contain the exact filename (e.g. GEM-AKS-001-2.png) or a full Drive link.</li>
                <li>Images are always downloaded to the server when you run sync.</li>
                <li>Drive filenames (e.g. GEM-AKS-001-2.png) are used only to find the file in Drive. Locally we save as <strong>product-id-index.ext</strong> (e.g. 172-0.png) so the single folder works for all products and &quot;Sync images&quot; on the Products page can link them correctly.</li>
              </Box>
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 2, py: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                  ETA
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {SYNC_ETA_TEXT}
                </Typography>
              </Box>
            </Box>

            {/* Test image from Drive — verify resolution and download without full sync */}
            <SectionTitle>Test image from Drive</SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Enter a filename that exists in your Drive folder (e.g. from column D in your sheet) or a full Drive link. This tests resolve + download without running a full sync.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
              <TextField
                size="small"
                label="Image filename or Drive link"
                placeholder="e.g. GEM-AKS-001-1.png or https://drive.google.com/..."
                value={testImageFilename}
                onChange={(e) => setTestImageFilename(e.target.value)}
                sx={{ minWidth: 280 }}
                onKeyDown={(e) => e.key === 'Enter' && handleTestImage()}
              />
              <Button variant="outlined" onClick={handleTestImage} disabled={testImageLoading || !testImageFilename.trim()}>
                {testImageLoading ? 'Testing…' : 'Test image'}
              </Button>
            </Box>
            {testImageResult && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: testImageResult.error ? 'error.light' : 'success.light', color: testImageResult.error ? 'error.contrastText' : 'success.contrastText' }}>
                {testImageResult.error ? (
                  <>
                    <Typography variant="body2">{testImageResult.error}</Typography>
                    {(testImageResult.error.toLowerCase().includes('could not resolve') || testImageResult.error.toLowerCase().includes('file not found') || testImageResult.error.toLowerCase().includes('set image folder')) && (
                      <Typography component="ul" variant="caption" sx={{ mt: 1, pl: 2 }}>
                        <li>Filename must match exactly (case and extension) a file in the Drive folder.</li>
                        <li>Image folder ID (step 4) must be the folder that contains the image files.</li>
                        <li>Sign in with Google on this page, or ensure the Drive folder is shared with your service account.</li>
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Resolved from Drive</Typography>
                    {testImageResult.url && <Typography variant="caption" component="div" sx={{ wordBreak: 'break-all', mt: 0.5 }}>URL: {testImageResult.url}</Typography>}
                    {testImageResult.downloaded && testImageResult.localPath && (
                      <>
                        <Typography variant="body2" sx={{ mt: 1 }}>Downloaded to server: {testImageResult.localPath}</Typography>
                        <Box component="img" src={API_BASE + testImageResult.localPath} alt="Test" sx={{ mt: 1, maxWidth: 200, maxHeight: 200, border: 1, borderColor: 'divider', borderRadius: 1 }} onError={(e) => { e.target.style.display = 'none'; }} />
                      </>
                    )}
                  </>
                )}
              </Box>
            )}
            {testImageResult && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Uses the same Drive folder ID as above. Sign in with Google or use a service account with the folder shared.
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* 5. Run sync */}
            <SectionTitle>5. Run sync</SectionTitle>
            {sheetNames.length === 0 && !loading && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Go to <strong>step 3 (Sheet selection)</strong> above, then click &quot;Load sheets&quot; to fetch your spreadsheet tabs and select which to sync. The Sync button will enable after that.
              </Alert>
            )}
            {loading && <LinearProgress sx={{ mb: 1, maxWidth: 400 }} />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleSheetsSync}
                disabled={loading || !spreadsheetId.trim() || sheetNames.length === 0 || selectedSheets.length === 0}
              >
                {loading ? 'Syncing…' : 'Sync from Sheets'}
              </Button>
              {loading && (
                <Button variant="outlined" color="error" onClick={handleStopSync} size="small">
                  Stop sync
                </Button>
              )}
              {loading && (
                <Typography variant="body2" color="text.secondary">
                  Usually 30–90 seconds. Click &quot;Stop sync&quot; (red bar at top or here) to cancel.
                </Typography>
              )}
            </Box>
            {sheetsError && <Alert severity="error" sx={{ mt: 2 }}>{sheetsError}</Alert>}
            <SummaryAlert result={sheetsResult} />
          </Box>
      </CardContent>
    </Card>
  );
}
